import Quill from 'quill';
import 'quill-emoji';
import ImageResize from 'quill-image-resize-module';
import FileDrop from './fileDrop';
import Embedder from '../embedder';
import { EmbedSelectionRenderer, CustomEmbedRenderer, ImageAttributeRenderer } from '../reactLoader';
import Utils from '../utils';

Quill.register('modules/fileDrop', FileDrop);

Quill.register('modules/imageResize', ImageResize);

const bold = Quill.import('formats/bold');
bold.tagName = 'b';
Quill.register(bold, true);

const italic = Quill.import('formats/italic');
italic.tagName = 'i';
Quill.register(italic, true);

let BlockEmbed = Quill.import('blots/block/embed');
let Inline = Quill.import('blots/inline');

const Break = Quill.import('blots/break');
const Embed = Quill.import('blots/embed');

Break.prototype.insertInto = function (parent, ref) {
  Embed.prototype.insertInto.call(this, parent, ref);
};

Break.prototype.length = function () {
  return 1;
};

Break.prototype.value = function () {
  return '\n';
};

Break.prototype.format = function () {
  return;
};

const contentUpdated = () => {
  document.dispatchEvent(new CustomEvent('quill-embed-load', { bubbles: true }));
};

class EmbedBlot extends BlockEmbed {
  static create(data) {
    let node = super.create();
    let url = data.url || data;
    if (url && url.endsWith('/')) {
      url = url.replace(/\/$/, '');
      data.url = url;
    }
    node.dataset.url = url;
    node.dataset.fileName = data.fileName || url || uuid || '';
    const uuid = typeof data === 'string' ? new URL(data).pathname.split('/')[2] : data.uuid;
    node.dataset.uuid = uuid && uuid !== 'undefined' && uuid.length === 36 ? uuid : Utils.generateElementID();

    const assertEmbedNode = () => {
      const embedNode = document.querySelector(`.oembed[data-uuid='${uuid}']`);
      if (embedNode && !node.parentNode) {
        node = embedNode;
      }
    };

    const showEmbedSelection = () => {
      const updateNode = (html) => {
        assertEmbedNode();
        if (node.parentNode) {
          node.outerHTML = html;
        }
        contentUpdated();
      };
      const customEmbed = (fileData) => {
        assertEmbedNode();
        node.className = 'file';
        contentUpdated();
        insertCustomLink(node, fileData);
      };
      const nodeData = { ...data, ...node.dataset };
      EmbedSelectionRenderer.render(nodeData, updateNode, customEmbed);
    };

    if (data.newEmbed) {
      showEmbedSelection();
    } else if (data.templateId) {
      data.fileName = data.fileName || url;
      insertCustomLink(node, data);
    } else {
      Embedder.embed(url).then((response) => {
        if (response.result !== 'OEMBED_MISSING') {
          node.innerHTML = response.html;
          node.style.height = response.height;
          const iframe = node.querySelector('iframe');
          if (iframe && !data.processed) {
            iframe.setAttribute('title', node.dataset.fileName || iframe.src);
            iframe.addEventListener('load', function () {
              node.dataset.processed = true;
              contentUpdated();
            });
          }
        }
      });
    }

    return node;
  }

  static value(domNode) {
    return domNode.dataset;
  }
}
EmbedBlot.blotName = 'oembed';
EmbedBlot.tagName = 'div';
EmbedBlot.className = 'oembed';
Quill.register(EmbedBlot);

class IframeEmbed extends BlockEmbed {
  static create(value) {
    const node = super.create(value);
    node.innerHTML = value;
    const iframe = node.querySelector('iframe');
    if (iframe) {
      iframe.setAttribute('title', iframe.src);
    }
    return node;
  }

  static value(node) {
    return node.innerHTML;
  }
}
IframeEmbed.blotName = 'iframe';
IframeEmbed.className = 'iframe';
IframeEmbed.tagName = 'div';
Quill.register(IframeEmbed);

class DividerBlot extends BlockEmbed {}
DividerBlot.blotName = 'divider';
DividerBlot.tagName = 'hr';
Quill.register(DividerBlot);

class WebcamBlot extends BlockEmbed {
  static create(data) {
    const cameraTagUrl = (window.editor || window.viewer || window.tp).options.cameraTag.url;
    const node = super.create();
    node.dataset.uuid = data.uuid;

    node.innerHTML = `
      <video controls class="lp--webcamRecording" poster="${cameraTagUrl}/assets/${data.uuid}/qvga_thumb.jpg">
        <source src="${cameraTagUrl}/downloads/${data.uuid}/qvga/mp4.mp4" type="video/mp4">
      </video>`;

    if (data.state === 'published') {
      node.dataset.state = 'processing';
      node.appendChild($('#camera-processing-screen').clone()[0]);
    }

    return node;
  }

  static value(domNode) {
    return domNode.dataset;
  }
}
WebcamBlot.blotName = 'webcam';
WebcamBlot.tagName = 'div';
WebcamBlot.className = 'webcam';
Quill.register(WebcamBlot);

class MicrophoneBlot extends BlockEmbed {
  static create(data) {
    const cameraTagUrl = (window.editor || window.viewer || window.tp).options.cameraTag.url;
    const node = super.create();
    node.dataset.uuid = data.uuid;

    node.innerHTML = `
      <audio controls class="lp--audioRecording">
        <source src="${cameraTagUrl}/assets/${data.uuid}/mp3.mp3" type="audio/mpeg">
      </audio>`;

    if (data.state === 'published') {
      node.dataset.state = 'processing';
    }

    return node;
  }

  static value(domNode) {
    return domNode.dataset;
  }
}
MicrophoneBlot.blotName = 'microphone';
MicrophoneBlot.tagName = 'div';
MicrophoneBlot.className = 'microphone';
Quill.register(MicrophoneBlot);

class AudioBlot extends BlockEmbed {
  static create(data) {
    const fileserviceUrl = (window.editor || window.viewer || window.tp).options.fileservice.url;
    const node = super.create();
    const fileName = fileserviceUrl + data.uuid;
    node.dataset.uuid = data.uuid;

    node.innerHTML = `
      <audio controls class="audio-attachment">
        <source src="${fileName}.mp3" type="audio/mpeg">
        <source src="${fileName}.ogg" type="audio/ogg">
        <source src="${fileName}.wav" type="audio/x-wav">
      </audio>`;

    return node;
  }

  static value(domNode) {
    return domNode.dataset;
  }
}
AudioBlot.blotName = 'audio';
AudioBlot.tagName = 'div';
AudioBlot.className = 'audio';
Quill.register(AudioBlot);

const insertCustomLink = (node, data) => {
  const { url, fileName, title, description, imageUrl, templateId, uuid } = data;
  node.dataset.url = url || '';
  node.dataset.fileName = fileName || '';
  node.dataset.title = title || '';
  node.dataset.description = description || '';
  node.dataset.imageUrl = imageUrl || '';
  node.dataset.templateId = templateId || '';
  node.dataset.uuid = uuid || Utils.generateElementID();
  node.setAttribute('contenteditable', false);
  const id = data.id || `fileEmbed_${Utils.generateElementID()}`;
  const onRender = () => {
    const quill = findQuill(node);
    quill?.updateContents(new Delta([{ retain: quill?.getLength() || 0 }, { insert: '\n' }]), Quill.sources.USER);
  };
  CustomEmbedRenderer.render(id, node, data, onRender);
  return node;
};

const findQuill = (node) => {
  while (node) {
    const quill = Quill.find(node);
    if (quill instanceof Quill) {
      return quill;
    }
    node = node.parentElement;
  }
};

class CustomFileBlot extends BlockEmbed {
  static create(data) {
    const node = super.create();
    const oldDelimiter = ':_:';
    let fileData = data;
    if (data.url.includes(oldDelimiter)) {
      const oldData = data.url.split(oldDelimiter);
      fileData = {
        url: oldData[0],
        fileName: oldData[1],
        templateId: 'template_0',
      };
    }
    insertCustomLink(node, fileData);
    return node;
  }

  static value(domNode) {
    return domNode.dataset;
  }
}
CustomFileBlot.blotName = 'file';
CustomFileBlot.tagName = 'div';
CustomFileBlot.className = 'file';
Quill.register(CustomFileBlot);

class ProgressBlot extends BlockEmbed {
  static create(key) {
    const node = super.create();
    node.dataset.key = key;
    node.classList.add('lp--activityEditorProgress');
    node.id = `upload-progress-${key}`;
    node.setAttribute('contenteditable', false);
    node.innerHTML = `<label class="lp--activityEditorProgressLabel">0%</label><div class="lp--activityEditorProgressBar"></div>`;
    return node;
  }

  static value(domNode) {
    return domNode.dataset.key;
  }
}
ProgressBlot.blotName = 'progress';
ProgressBlot.tagName = 'div';
ProgressBlot.className = 'progress';
Quill.register(ProgressBlot);

class ConversionBlot extends BlockEmbed {
  static create(key) {
    const node = super.create();
    node.dataset.key = key;
    node.classList.add('lp--activityEditorProgress');
    node.id = `conversion-progress-${key}`;
    node.setAttribute('contenteditable', false);
    node.innerHTML = `<div class="media-processing__icon"></div><label class="lp--activityEditorProgressLabel"></label>`;
    return node;
  }

  static value(domNode) {
    return domNode.dataset.key;
  }
}
ConversionBlot.blotName = 'conversion';
ConversionBlot.tagName = 'div';
ConversionBlot.className = 'conversion';
Quill.register(ConversionBlot);

class Highlight extends Inline {
  static create(value) {
    const node = super.create(value);
    node.dataset['selectionUuid'] = value;
    return node;
  }

  static formats(domNode) {
    return domNode.dataset['selectionUuid'];
  }
}
Highlight.blotName = 'highlight';
Highlight.className = 'highlight';
Highlight.tagName = 'mark';
Quill.register(Highlight);

Quill.import('blots/block');

const Clipboard = Quill.import('modules/clipboard');
const Delta = Quill.import('delta');

class PowerClipboard extends Clipboard {
  onPaste(e) {
    if (e.defaultPrevented || !this.quill.isEnabled()) {
      return;
    }

    const clipboardDataItems = e.clipboardData && e.clipboardData.items;
    for (let index = 0; index < clipboardDataItems.length; index++) {
      if (clipboardDataItems[index].kind === 'file') {
        e.preventDefault();
      }
    }

    const embedInlineContentOnNewLine = (op, index) => {
      if (op.insert && typeof op.insert === 'string' && !op.insert.endsWith('\n')) {
        const nextOp = delta.ops[index + 1];
        if (nextOp && nextOp.attributes && nextOp.attributes.link && nextOp.insert && nextOp.insert.oembed) {
          op.insert = op.insert + '\n';
        }
      }
    };

    const removeLineBreakFormatting = (op) => {
      if (op.attributes && op.insert && op.insert.break === '') {
        delete op.attributes;
      }
    };

    const range = this.quill.getSelection();
    let delta = new Delta().retain(range.index);
    const scrollTop = document.querySelector('.content-panel')?.scrollTop || 0;
    this.container.focus();
    this.quill.selection.update(Quill.sources.SILENT);
    setTimeout(async () => {
      delta = delta.concat(this.convert()).delete(range.length);
      delta.forEach((op, index) => {
        embedInlineContentOnNewLine(op, index);
        removeLineBreakFormatting(op);
      });
      this.quill.updateContents(delta, Quill.sources.USER);
      this.quill.setSelection(delta.length() - range.length, Quill.sources.SILENT);
      document.querySelector('.content-panel')?.scrollTo(0, scrollTop);
      this.quill.focus();
    }, 1);
  }
}

Quill.register('modules/clipboard', PowerClipboard, true);

const BaseImageFormat = Quill.import('formats/image');
const ImageFormatAttributesList = ['src', 'alt', 'height', 'width', 'style'];

class ImageFormat extends BaseImageFormat {
  static formats(domNode) {
    return ImageFormatAttributesList.reduce(function (formats, attribute) {
      if (domNode.hasAttribute(attribute)) {
        formats[attribute] = domNode.getAttribute(attribute);
      }
      if (!domNode.hasAttribute('role')) {
        domNode.setAttribute('role', 'presentation');
      }
      return formats;
    }, {});
  }
  format(name, value) {
    if (ImageFormatAttributesList.indexOf(name) > -1) {
      if (value) {
        this.domNode.setAttribute(name, value);
      } else {
        this.domNode.removeAttribute(name);
      }
    } else {
      super.format(name, value);
    }
  }
}

Quill.register(ImageFormat, true);

export class ImageAttribute {
  constructor(resizer) {
    this.resizer = resizer;
    this.overlay = resizer.overlay;
    this.img = resizer.img;
    this.options = resizer.options;

    this.options.editImageAttributesContainerStyle = {
      position: 'absolute',
      bottom: '12px',
      right: '0',
      left: '0',
      height: '0',
      minWidth: '100px',
      font: '12px/1.0 Arial, Helvetica, sans-serif',
      textAlign: 'center',
      color: '#333',
      boxSizing: 'border-box',
      cursor: 'default',
    };
    this.options.editImageAttributesButtonStyle = {
      display: 'inline-block',
      width: '24px',
      height: '24px',
      background: 'white',
      border: '1px solid #999',
      color: '#444',
      padding: '2px',
      verticalAlign: 'middle',
      cursor: 'pointer',
    };
  }

  onCreate = () => {
    const editImageAttributesIcon = `
      <svg xmlns="http://www.w3.org/2000/svg" width="18.478" height="18.107" viewBox="0 0 18.478 18.107">
        <path d="M4343.041,1127.325a.45.45,0,0,0-.106-.141.719.719,0,0,0-.131-.093,2.831,2.831,0,0,0-.256-.128c-.345-.155-.878-.36-1.333-.53-.268-.1-.505-.186-.658-.241l-.3-.728c.087-.2.245-.565.4-.947.12-.288.241-.583.333-.829.046-.123.086-.234.116-.331.015-.048.027-.093.038-.138a.8.8,0,0,0,.022-.163.454.454,0,0,0-.015-.114.42.42,0,0,0-.115-.2h0l-1.373-1.339h0a.438.438,0,0,0-.2-.109.464.464,0,0,0-.112-.013.749.749,0,0,0-.167.022,2.57,2.57,0,0,0-.273.086c-.356.126-.882.351-1.332.548-.267.117-.5.222-.649.289l-.752-.3c-.089-.217-.255-.62-.431-1.03-.128-.3-.261-.6-.373-.832-.058-.117-.108-.216-.159-.3a.931.931,0,0,0-.086-.125.475.475,0,0,0-.081-.082.413.413,0,0,0-.091-.056.424.424,0,0,0-.182-.04h-1.936a.425.425,0,0,0-.181.04.484.484,0,0,0-.144.1.687.687,0,0,0-.094.129c-.044.074-.086.155-.132.253-.16.338-.371.86-.545,1.308-.1.261-.189.492-.246.641l-.751.3c-.2-.086-.579-.242-.976-.4-.294-.117-.6-.235-.847-.325-.126-.045-.238-.083-.336-.112-.049-.015-.095-.027-.14-.037a.745.745,0,0,0-.16-.02.441.441,0,0,0-.109.012.43.43,0,0,0-.2.114v0l-1.369,1.342h0a.431.431,0,0,0-.115.2.457.457,0,0,0-.016.121.7.7,0,0,0,.025.168,2.308,2.308,0,0,0,.087.27c.13.348.36.865.56,1.3.118.256.223.482.291.627l-.3.726c-.221.085-.635.249-1.055.422-.306.126-.61.255-.849.365-.119.055-.221.1-.3.152a.911.911,0,0,0-.127.083.471.471,0,0,0-.085.079.573.573,0,0,0-.059.091.444.444,0,0,0-.045.189v1.9a.446.446,0,0,0,.042.185.453.453,0,0,0,.107.142.774.774,0,0,0,.131.095c.074.043.157.084.257.13.345.156.879.362,1.335.533.27.1.506.187.659.242l.305.724c-.089.2-.247.566-.408.949-.119.287-.239.583-.331.828-.046.123-.086.234-.116.329a1.3,1.3,0,0,0-.039.137.662.662,0,0,0-.021.164.449.449,0,0,0,.012.111.44.44,0,0,0,.117.206h0l1.373,1.342h0a.433.433,0,0,0,.2.112.452.452,0,0,0,.109.012.758.758,0,0,0,.167-.022,2.773,2.773,0,0,0,.271-.086c.353-.127.881-.351,1.332-.548.268-.118.5-.222.65-.289l.756.305c.088.216.252.619.43,1.03.128.3.261.6.373.832.057.117.108.216.159.3a1.071,1.071,0,0,0,.086.126.55.55,0,0,0,.083.082.353.353,0,0,0,.09.056.427.427,0,0,0,.179.04h1.938a.433.433,0,0,0,.181-.041.464.464,0,0,0,.142-.1.668.668,0,0,0,.1-.13,2.571,2.571,0,0,0,.133-.253c.16-.338.369-.861.544-1.308.1-.261.189-.492.245-.641l.755-.3c.2.086.578.242.975.4.294.118.595.235.845.326.125.045.238.083.335.112.049.015.094.027.138.037a.724.724,0,0,0,.162.02.4.4,0,0,0,.107-.013.421.421,0,0,0,.2-.1l.01-.01,1.373-1.341,0,0a.425.425,0,0,0,.113-.2.383.383,0,0,0,.014-.113.706.706,0,0,0-.025-.169,2.337,2.337,0,0,0-.087-.27c-.13-.348-.361-.865-.561-1.306-.117-.256-.223-.481-.291-.627l.3-.725c.222-.086.636-.249,1.058-.423.3-.126.611-.256.848-.366.121-.056.221-.105.305-.153a1.112,1.112,0,0,0,.127-.083.424.424,0,0,0,.083-.08.419.419,0,0,0,.059-.09.4.4,0,0,0,.044-.188v-1.9A.4.4,0,0,0,4343.041,1127.325Zm-9.195,4.673a3.5,3.5,0,1,1,3.5-3.5A3.5,3.5,0,0,1,4333.846,1132Z" transform="translate(-4324.607 -1119.449)" fill="currentColor" />
      </svg>`;

    const deleteImageIcon = `
      <svg xmlns="http://www.w3.org/2000/svg" x="0" y="0" viewBox="-4 -1 45.6 52.08">
        <g>
          <path fill="currentColor" d="M4.7,40c0.1,2,1.8,3.5,3.9,3.5h21c2.1,0,3.7-1.5,3.8-3.5l2-25.6H2.7L4.7,40z" />
          <path fill="currentColor" d="M36.8,2.8H27l-0.3-1.9c-0.1-0.5-0.5-0.9-1-0.9H12.5c-0.5,0-0.9,0.4-1,0.9l-0.3,1.9h-10C0.6,2.8,0,3.3,0,4v4.4c0,0.7,0.6,1.2,1.2,1.2h35.6c0.7,0,1.2-0.6,1.2-1.2V4C38,3.3,37.5,2.8,36.8,2.8z" />
        </g>
      </svg>`;

    this.toolbar = document.createElement('div');
    Object.assign(this.toolbar.style, this.options.editImageAttributesContainerStyle);

    const addButton = (title, icon, eventHandler, style = {}) => {
      const button = document.createElement('span');
      button.setAttribute('role', 'img');
      button.setAttribute('title', title);
      Object.assign(button.style, this.options.editImageAttributesButtonStyle, style);
      button.innerHTML = icon;
      button.addEventListener('click', eventHandler);
      this.toolbar.appendChild(button);
    };
    addButton(
      i18n.__('app_edit'),
      editImageAttributesIcon,
      () => {
        ImageAttributeRenderer.render(this.img);
      },
      { marginRight: '24px' },
    );
    addButton(i18n.__('app_delete'), deleteImageIcon, () => {
      this.resizer.hide();
      this.img.remove();
    });

    this.overlay.appendChild(this.toolbar);
  };

  onDestroy = () => {};
  onUpdate = () => {};
}

export default Quill;
